/**
 * Filmstrip.jsx
 *
 * Colonna verticale di miniature pagina per la navigazione del catalogo.
 * Ogni miniatura è un GridPage scalato tramite CSS transform.
 * La pagina corrente è evidenziata con bordo blu.
 * Scroll verticale senza scrollbar visibile.
 *
 * Props:
 *   pages        {object[][]} — array di pagine da usePacking
 *   currentPage  {number}     — pagina corrente (1-based)
 *   onPageClick  {function}   — callback(pageNum)
 *   config       {object}     — config layout
 *   gridCols, gridRows, pageWidthMm, pageHeightMm, gapMm — opzionali, usa default A4
 */

import { useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { GridPage } from './GridViewBuilder'

// ─── Costanti ────────────────────────────────────────────────────────────────

const PAGE_W_MM   = 175
const PAGE_H_MM   = 250
const PAGE_GAP_MM = 3.5
const GRID_COLS   = 6
const GRID_ROWS   = 12

// Dimensioni miniatura visibile
const THUMB_W   = 72
const THUMB_H   = Math.round(THUMB_W * PAGE_H_MM / PAGE_W_MM)  // ≈ 103px

// GridPage viene renderizzato a INNER_W px di larghezza, poi scalato con CSS.
// INNER_W deve essere abbastanza grande perché i font delle tabelle (6.5pt min)
// superino il minimo del browser (~10px): ptToPx(6.5, scale) >= 10 → scale >= 4.36
// → INNER_W >= 175 * 4.36 + 24(padding) ≈ 787. Usiamo 800 con margine di sicurezza.
const INNER_W   = 800
const CSS_SCALE = THUMB_W / INNER_W  // ≈ 0.09

// ─── Filmstrip ────────────────────────────────────────────────────────────────

const DOT_COLORS = {
  overflow:  '#f59e0b',
  imageFit:  '#5C6BC0',
  spaceFill: '#26A69A',
}

export function Filmstrip({
  pages        = [],
  currentPage  = 1,
  onPageClick,
  config,
  hasCover     = false,
  onOverflow   = null,
  overflowSet          = new Set(),
  activeAdvisors       = new Set(),
  imageFitSuggestions   = new Map(),
  spaceFillSuggestions  = new Map(),
  gridCols     = GRID_COLS,
  gridRows     = GRID_ROWS,
  pageWidthMm  = PAGE_W_MM,
  pageHeightMm = PAGE_H_MM,
  gapMm        = PAGE_GAP_MM,
  hasBanner    = false,
}) {
  const theme = useTheme()
  const primaryColor = theme.palette.primary.main
  const activeRef = useRef(null)

  // Scrolla la miniatura attiva nel campo visivo al cambio pagina
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [currentPage])

  return (
    <Box
      sx={{
        width:          THUMB_W + 16,
        height:         '100%',
        overflowY:      'scroll',
        scrollbarWidth: 'none',           // Firefox
        '&::-webkit-scrollbar': { display: 'none' },  // Chrome/Safari
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        gap:            '8px',
        padding:        '8px 4px',
        boxSizing:      'border-box',
        backgroundColor: '#ffffff',
        borderRight:    '1px solid #e0e0e0',
      }}
    >
      {pages.map((page, i) => {
        const pageNum  = i + 1
        const isActive = pageNum === currentPage

        // Calcola pallini per questa pagina (fino a 3: overflow, imageFit, spaceFill)
        const indices = page.map(p => p.originalIndex).filter(idx => idx != null)
        const dots = []
        const hasSlotOverflow = (idx) => overflowSet.has(idx) || [...overflowSet].some(k => typeof k === 'string' && k.startsWith(`${idx}:`))
        const hasSlotSuggestion = (suggs, idx) => {
          const s = suggs.get(idx)
          if (s && s.status === 'pending') return true
          return [...suggs.entries()].some(([k, v]) => typeof k === 'string' && k.startsWith(`${idx}:`) && v.status === 'pending')
        }
        if (indices.some(idx => hasSlotOverflow(idx))) dots.push(DOT_COLORS.overflow)
        if (activeAdvisors.has('imageFit') && indices.some(idx => hasSlotSuggestion(imageFitSuggestions, idx)))
          dots.push(DOT_COLORS.imageFit)
        if (activeAdvisors.has('spaceFill') && indices.some(idx => hasSlotSuggestion(spaceFillSuggestions, idx)))
          dots.push(DOT_COLORS.spaceFill)

        return (
          <div
            key={i}
            ref={isActive ? activeRef : null}
            onClick={() => onPageClick?.(pageNum)}
            title={`Page ${pageNum}`}
            style={{
              display:       'flex',
              flexDirection: 'column',
              alignItems:    'center',
              flexShrink:    0,
              cursor:        'pointer',
              gap:           3,
            }}
          >
            {/* Miniatura */}
            <div style={{
              width:        THUMB_W,
              height:       THUMB_H,
              border:       isActive ? `2px solid ${primaryColor}` : '2px solid #e0e0e0',
              borderRadius: 2,
              overflow:     'hidden',
              position:     'relative',
              boxSizing:    'border-box',
              background:   '#ffffff',
              transition:   'border-color 0.15s',
            }}>
              {/* GridPage scalato via CSS transform — pointerEvents disabilitati */}
              <div style={{
                position:        'absolute',
                top:             0,
                left:            0,
                width:           INNER_W,
                transformOrigin: 'top left',
                transform:       `scale(${CSS_SCALE})`,
                pointerEvents:   'none',
              }}>
                <GridPage
                  config={config}
                  page={page}
                  gridCols={gridCols}
                  gridRows={gridRows}
                  pageWidthMm={pageWidthMm}
                  pageHeightMm={pageHeightMm}
                  gapMm={gapMm}
                  debug={false}
                  hoveredIndex={null}
                  isCoverPage={hasCover && i === 0}
                  isFinalPage={hasBanner && i + 1 === pages.length}
                  onOverflow={onOverflow}
                />
              </div>
            </div>

            {/* Numero pagina + pallino indicatore */}
            <div style={{
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              gap:          4,
              lineHeight:   1,
            }}>
              <span style={{
                fontSize:     11,
                fontWeight:   isActive ? 700 : 400,
                color:        isActive ? primaryColor : '#9e9e9e',
                letterSpacing: '0.03em',
                transition:   'color 0.15s',
                userSelect:   'none',
              }}>
                {pageNum}
              </span>
              {dots.map((color, di) => (
                <span key={di} style={{
                  width:        6,
                  height:       6,
                  borderRadius: '50%',
                  background:   color,
                  flexShrink:   0,
                }} />
              ))}
            </div>
          </div>
        )
      })}
    </Box>
  )
}
